import { useState, useCallback } from "react";
import { intelligenceEngineApi } from "@services/extractionApi";
import type { UserInstructionViewModel } from "@models/extraction";

export const useUserInstructions = (doctypeCode: string) => {
  const [instructions, setInstructions] = useState<UserInstructionViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!doctypeCode) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await intelligenceEngineApi.listUserInstructions(doctypeCode);
      setInstructions(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load instructions");
    } finally {
      setIsLoading(false);
    }
  }, [doctypeCode]);

  const save = useCallback(
    async (name: string, instruction: string): Promise<UserInstructionViewModel | null> => {
      setIsSaving(true);
      setError(null);
      try {
        const created = await intelligenceEngineApi.saveUserInstruction({
          name,
          instruction,
          doctype_code: doctypeCode,
        });
        setInstructions((prev) => [...prev, created]);
        return created;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save instruction");
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [doctypeCode],
  );

  const update = useCallback(
    async (
      instructionId: string,
      name: string,
      instruction: string,
    ): Promise<UserInstructionViewModel | null> => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = await intelligenceEngineApi.updateUserInstruction(
          doctypeCode,
          instructionId,
          { name, instruction },
        );
        setInstructions((prev) =>
          prev.map((i) => (i.id === instructionId ? updated : i)),
        );
        return updated;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to update instruction");
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [doctypeCode],
  );

  const remove = useCallback(
    async (instructionId: string): Promise<boolean> => {
      try {
        await intelligenceEngineApi.deleteUserInstruction(doctypeCode, instructionId);
        setInstructions((prev) => prev.filter((i) => i.id !== instructionId));
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to delete instruction");
        return false;
      }
    },
    [doctypeCode],
  );

  return { instructions, isLoading, isSaving, error, load, save, update, remove };
};
